import { FC, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input } from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { AppLoader } from "../../shared/components/AppLoader";
import {
  SEVERITY_STYLES,
  TYPE_LABELS
} from "../../shared/components/DeficiencyDialog/deficiencyDisplay";
import { getAdminDeficiencies, type AdminDeficiency } from "../../shared/services/deficiencies";

const CURABLE_STATUSES = new Set(["open", "in_review"]);

type TProps = {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: { deficiencyId?: number; note?: string }) => void;
  isCreating: boolean;
};

export const NewDraftDialog: FC<TProps> = ({ open, onClose, onCreate, isCreating }) => {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [noDeficiency, setNoDeficiency] = useState(false);
  const [reason, setReason] = useState("");

  const { data: deficiencies = [], isLoading } = useQuery<AdminDeficiency[]>({
    queryKey: ["admin-deficiencies"],
    queryFn: () => getAdminDeficiencies(),
    enabled: open
  });

  const curable = useMemo(() => {
    const term = search.trim().toLowerCase();
    return deficiencies
      .filter((d) => d.subject === "case_brief" && CURABLE_STATUSES.has(d.status))
      .filter(
        (d) =>
          !term ||
          String(d.id).includes(term) ||
          d.description.toLowerCase().includes(term) ||
          (d.caseBriefName ?? "").toLowerCase().includes(term)
      );
  }, [deficiencies, search]);

  const canSubmit = noDeficiency ? reason.trim().length > 0 : selectedId !== null;

  const submit = () => {
    if (!canSubmit) return;
    onCreate(noDeficiency ? { note: reason.trim() } : { deficiencyId: selectedId as number });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New draft revision</DialogTitle>
        </DialogHeader>

        <FigmaTypography variant="body-3" className="text-gray-500">
          Prompt changes should trace back to a filed deficiency. Pick the one this draft will cure.
        </FigmaTypography>

        {!noDeficiency && (
          <>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by id, description or case brief…"
            />

            {isLoading ? (
              <AppLoader />
            ) : curable.length === 0 ? (
              <FigmaTypography variant="body-3" className="text-gray-500 py-4 text-center">
                No open case brief deficiencies.
              </FigmaTypography>
            ) : (
              <div className="max-h-[280px] divide-y overflow-y-auto rounded border border-gray-200">
                {curable.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setSelectedId(d.id)}
                    className={`flex w-full flex-col items-start gap-1 px-3 py-2 text-left hover:bg-gray-50 ${
                      selectedId === d.id ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-gray-900">#{d.id}</span>
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[11px] leading-[16px] ${SEVERITY_STYLES[d.severity]}`}
                      >
                        {d.severity}
                      </span>
                      <span className="text-[11px] text-gray-400">{d.typeLabel ?? TYPE_LABELS[d.type] ?? d.type}</span>
                    </div>
                    <div className="line-clamp-2 text-[13px] text-gray-600">{d.description}</div>
                    {d.caseBriefName && <div className="text-[11px] text-gray-400">{d.caseBriefName}</div>}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <label className="flex items-center gap-2 text-[13px] text-gray-600">
          <input
            type="checkbox"
            checked={noDeficiency}
            onChange={(e) => setNoDeficiency(e.target.checked)}
            className="h-4 w-4"
          />
          No deficiency — maintenance change
        </label>

        {noDeficiency && (
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why does this change have no filed deficiency?"
            className="h-20 w-full resize-y rounded border border-gray-200 px-3 py-2 text-[13px] text-gray-800 outline-none focus:ring-1 focus:ring-gray-300"
          />
        )}

        <div className="flex items-center justify-end gap-2">
          <AppButton appearance="outlined" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton onClick={submit} disabled={!canSubmit} isLoading={isCreating} loadingText="Creating...">
            Create draft
          </AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
